import { COPY } from '../copy';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { WIDE_LAYOUT_QUERY } from './layout';
import styles from './LoadingSkeleton.module.css';

/** The nav's outline before the config says what its groups are: two groups, as many items as a typical form */
const NAV_GROUPS = [6, 4];

/** A block's worth of empty fields: two rows of the grid at desktop width */
const FIELDS = 6;

/**
 * Shown while the config loads (and as the lazy route's fallback). It draws the page the form is about to become, the
 * header card, the nav and the section with its field grid, in the same cards and spacing as the shell, so nothing
 * jumps when the form arrives.
 */
export function LoadingSkeleton() {
  const wide = useMediaQuery(WIDE_LAYOUT_QUERY);
  return (
    <div className={styles.skeleton} role="status" aria-busy="true">
      <span className={styles.visuallyHidden}>{COPY.loading}</span>
      <div className={styles.headerCard} aria-hidden="true">
        <div className={styles.header}>
          <span className={`${styles.bone} ${styles.rule}`} />
          <div className={styles.headerText}>
            <span className={`${styles.bone} ${styles.title}`} />
            <span className={`${styles.bone} ${styles.subtitle}`} />
            <div className={styles.details}>
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.detail}>
                  <span className={`${styles.bone} ${styles.detailLabel}`} />
                  <span className={`${styles.bone} ${styles.detailValue}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.progress}>
          <div className={styles.progressRow}>
            <span className={`${styles.bone} ${styles.progressLabel}`} />
            <span className={`${styles.bone} ${styles.progressPercent}`} />
          </div>
          <span className={`${styles.bone} ${styles.track}`} />
          <span className={`${styles.bone} ${styles.progressNote}`} />
        </div>
      </div>
      <div className={wide ? styles.bodyWide : styles.bodyNarrow} aria-hidden="true">
        {wide ? (
          <div className={styles.sidebar}>
            {NAV_GROUPS.map((items, group) => (
              <div key={group} className={styles.group}>
                <span className={`${styles.bone} ${styles.groupLabel}`} />
                {Array.from({ length: items }, (_, i) => (
                  <div key={i} className={styles.navItem}>
                    <span className={`${styles.bone} ${styles.dot}`} />
                    <span className={`${styles.bone} ${styles.navTitle}`} />
                    <span className={`${styles.bone} ${styles.navCount}`} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.stepper}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={styles.step}>
                <span className={`${styles.bone} ${styles.dot}`} />
                <span className={`${styles.bone} ${styles.stepTitle}`} />
              </div>
            ))}
          </div>
        )}
        <div className={styles.card}>
          <div className={styles.head}>
            <div className={styles.titles}>
              <span className={`${styles.bone} ${styles.stepLabel}`} />
              <span className={`${styles.bone} ${styles.sectionTitle}`} />
              <span className={`${styles.bone} ${styles.sectionSubtitle}`} />
            </div>
            <span className={`${styles.bone} ${styles.pill}`} />
          </div>
          <div className={styles.form}>
            {[FIELDS, FIELDS / 2].map((fields, block) => (
              <div key={block} className={styles.block}>
                <span className={`${styles.bone} ${styles.blockTitle}`} />
                <div className={styles.grid}>
                  {Array.from({ length: fields }, (_, i) => (
                    <div key={i} className={styles.field}>
                      <span className={`${styles.bone} ${styles.label}`} />
                      <span className={styles.input}>
                        <span className={`${styles.bone} ${styles.value}`} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
